import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileText, Calendar, User, Clock, ArrowRight, Mail, Phone, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import Header from "../../components/Public/ui/Header";
import Footer from "../../components/Public/ui/Footer";
import DemoToolWidget from "../../components/DemoToolWidget";
import NeedAssistanceBanner from "../../components/common/NeedAssistanceBanner";

export default function RenewalPage() {
    const location = useLocation();
    const navigate = useNavigate();

    const [renewalData, setRenewalData] = useState(location.state?.renewalData || null);
    const [loading, setLoading] = useState(!location.state?.renewalData);

    useEffect(() => {
        if (!location.state?.renewalData) {
            const fetchUserDetails = async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/user`, { credentials: "include" });
                    const result = await response.json();
                    if (result.success && result.status === "approved_can_renew") {
                        setRenewalData({
                            applicationData: result.applicationData,
                            userData: result.userData
                        });
                    } else {
                        navigate("/passport/application", { replace: true });
                    }
                } catch (err) {
                    console.error("Failed to load user data:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchUserDetails();
        }
    }, [navigate, location.state]);

    const handleRenewalStart = () => {
        navigate("/apply", {
            state: { isRenewal: true, renewalData },
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
                <Header />
                <main className="flex-grow flex items-center justify-center p-4">
                    <p className="text-slate-500 font-medium animate-pulse">Loading renewal details...</p>
                </main>
                <Footer />
            </div>
        );
    }

    const applicationData = renewalData?.applicationData || {};
    const userData = renewalData?.userData || {};

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            <Header />

            <main className="flex-grow flex items-center justify-center p-4 sm:p-6"
                style={{
                    backgroundImage: "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(241,245,249,1) 100%), url('/assets/Images/SLUDI-background.png')",
                    backgroundSize: "cover",
                    backgroundAttachment: "fixed"
                }}>

                <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                    {/* Service Header */}
                    <div className="bg-blue-600 p-6 sm:px-8 text-white">
                        <div className="flex items-center gap-5">
                            <div className="bg-white/20 p-3 rounded-xl backdrop-blur-md border border-white/30">
                                <RefreshCw className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold leading-tight">Passport renewal</h2>
                                <p className="text-blue-100 text-sm mt-0.5">Your current passport is eligible for renewal. Review your details below.</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-6">

                        {/* 1. Current Passport Information */}
                        <section className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                    <ShieldCheck size={14} className="text-blue-600" /> Current passport information
                                </h3>
                                <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full uppercase">
                                    Expires soon
                                </span>
                            </div>
                            <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-6">
                                <SummaryItem label="Document No" value={applicationData?.application_number} isMono />
                                <SummaryItem label="Travel Type" value={applicationData?.travel_document_type} />
                                <SummaryItem label="Issued on" value={applicationData?.issued_date ? new Date(applicationData.issued_date).toLocaleDateString() : '—'} />
                                <SummaryItem label="Expiry date" value={applicationData?.expiry_date ? new Date(applicationData.expiry_date).toLocaleDateString() : '—'} highlight />
                            </div>
                        </section>

                        {/* 2. Eligibility & Requirements Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-slate-50 border border-slate-100 rounded-xl p-5">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <User size={14} /> Eligibility
                                </h3>
                                <ul className="space-y-2">
                                    <ListItem text="Current holder of an active passport" />
                                    <ListItem text="Passport expiring in less than 6 months" />
                                </ul>
                            </div>
                            <div className="bg-slate-50 border border-slate-100 rounded-xl p-5">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <FileText size={14} /> What you'll need
                                </h3>
                                <ul className="space-y-2">
                                    <ListItem text="Current original passport" />
                                    <ListItem text="Recent SLUDI verified photo" />
                                </ul>
                            </div>
                        </div>

                        {/* 3. Primary Action Card */}
                        <section className="bg-white rounded-2xl p-6 border border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
                            <div className="text-left">
                                <h3 className="text-lg font-bold text-slate-900 leading-none">
                                    Ready to renew?
                                </h3>
                                <p className="text-slate-500 text-sm mt-2 max-w-md leading-normal">
                                    The renewal process will update your passport validity for another 10 years.
                                </p>
                            </div>
                            <div className="shrink-0 w-full sm:w-auto">
                                <button
                                    onClick={handleRenewalStart}
                                    className="flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-[#1642b3] text-white rounded-lg font-semibold text-sm hover:bg-blue-800 transition-all active:scale-95"
                                >
                                    Start renewal process
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </section>

                        {/* 4. Support Footer */}
                        <NeedAssistanceBanner type="info" />
                    </div>
                </div>
            </main>

            <Footer />
            <DemoToolWidget context="approved" applicationData={applicationData} />
        </div>
    );
}

/* Internal Components */
function SummaryItem({ label, value, isMono = false, highlight = false }) {
    return (
        <div className="flex flex-col">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-tight">{label}</span>
            <span className={`font-bold leading-tight truncate ${highlight ? 'text-amber-600 text-base' : 'text-slate-800 text-sm'
                } ${isMono ? 'font-mono' : ''}`}>
                {value || '—'}
            </span>
        </div>
    );
}

function ListItem({ text }) {
    return (
        <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-slate-700 font-medium">{text}</span>
        </li>
    );
}

// Icon Helper
const RefreshCw = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
);